import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { putDataPromise } from 'brock-fetch-utils';
import { getEnrollmentUri } from '../constants/apiConstants';
import { orderBy } from 'natural-orderby';
import {
    AccentLabel,
    AnimatedPageLoader,
    HorizontalFlex,
    Label,
    PadContainer,
    Ticket,
    TicketBody,
    TicketHeader,
    VerticalFlex,
} from 'smartsuite-ui';
import { countriesEnglish } from '../constants/countries';
import { Trans, useTranslation } from 'react-i18next';
import { DateTimeFormatter } from 'brock-date-time';
import { formatPhoneNumber } from '../utils/formatters';

function ReservationConfirmationPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { reservationId, reservationLastName } = useParams();
    const enrollmentQuery = useQuery({
        queryKey: ['enrollment', reservationId, reservationLastName],
        queryFn: async () => {
            const response = await fetch(
                getEnrollmentUri(reservationId, reservationLastName),
            );

            if (!response.ok) {
                navigate('/');
            }
            const parsedResponse = await response.json();

            const orderedPassengers = orderBy(
                parsedResponse.passengers,
                ['isPrimary'],
                ['desc'],
            );

            return { ...parsedResponse, passengers: orderedPassengers };
        },
        onSuccess: (data) => {
            if (data.cancelledTimestamp) {
                navigate('cancelEnrollment');
            }
        },
    });
    const { data: enrollment } = enrollmentQuery;

    if (!enrollment) {
        return <AnimatedPageLoader />;
    }

    const primaryPassenger = enrollment.passengers.find(
        (passenger) => passenger.isPrimary,
    );

    return (
        <div className="page-wrapper confirmation-page">
            <div className="confirmation-card">
                <header className="confirmation-card__header">
                    <div className="confirmation-card__title confirmation-card__title--success">
                        {location.state?.hasModified
                            ? t('confirmationPageUpdatedTitle')
                            : t('confirmationPageTitle')}
                    </div>
                </header>
                <div className="confirmation-card__subtitle">
                    <span>{t('enrollmentDetailsSubtitle')}</span>
                </div>
                <hr className="confirmation-card__divider" />
                <div className="confirmation-card__container">
                    <PadContainer cssClassModifiers={['pad-small']}>
                        <VerticalFlex>
                            <HorizontalFlex
                                cssClassModifiers={['space-between']}
                            >
                                <span>{t('reservationNumber')}</span>
                                <span className="confirmation-card__enrollment-detail">
                                    {enrollment.reservationId}
                                </span>
                            </HorizontalFlex>
                            <HorizontalFlex
                                cssClassModifiers={['space-between']}
                            >
                                <span>{t('arrivalDate')}</span>
                                <span className="confirmation-card__enrollment-detail">
                                    {DateTimeFormatter.withFormatString(
                                        primaryPassenger.checkinDate,
                                        'MM/DD/YYYY',
                                    )}
                                </span>
                            </HorizontalFlex>
                            <HorizontalFlex
                                cssClassModifiers={['space-between']}
                            >
                                <span>{t('disneyResortHotel')}</span>
                                <span className="confirmation-card__enrollment-detail">
                                    {enrollment.resortName}
                                </span>
                            </HorizontalFlex>
                            <HorizontalFlex
                                cssClassModifiers={['space-between']}
                            >
                                <span>{t('guestEmail')}</span>
                                <span className="confirmation-card__enrollment-detail">
                                    {enrollment.primaryEmail}
                                </span>
                            </HorizontalFlex>
                            <HorizontalFlex
                                cssClassModifiers={['space-between']}
                            >
                                <span>{t('guestPhoneNumber')}</span>
                                <span className="confirmation-card__enrollment-detail">
                                    {formatPhoneNumber(enrollment.primaryPhone)}
                                </span>
                            </HorizontalFlex>
                        </VerticalFlex>
                    </PadContainer>
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                <div className="confirmation-card__subtitle">
                    {t('manageEnrollmentSubtitle')}
                </div>
                <hr className="confirmation-card__divider" />{' '}
                <div className="confirmation-card__container">
                    <PadContainer cssClassModifiers={['pad-small']}>
                        <ul>
                            <li>
                                <Link to="modifyFlightDetails">
                                    {t('modifyYourFlight')}
                                </Link>{' '}
                                {t('modifyYourFlightDescription')}
                            </li>
                            <li>
                                <Link to="./bagEvents">
                                    Track your checked luggage
                                </Link>{' '}
                                in real time along its journey
                            </li>
                        </ul>
                    </PadContainer>
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                <div className="confirmation-card__subtitle">
                    {t('guestDetailsSubtitle')}
                </div>
                <hr className="confirmation-card__divider" />
                <div className="confirmation-card__container">
                    <PadContainer cssClassModifiers={['pad-small']}>
                        <VerticalFlex>
                            {enrollment.passengers
                                .filter((pax) => pax.isEligible)
                                .map((passenger, index) => (
                                    <div key={passenger.paxId}>
                                        {index > 0 && (
                                            <PadContainer
                                                cssClassModifiers={[
                                                    'pad-small',
                                                ]}
                                            />
                                        )}
                                        <Ticket>
                                            <TicketHeader>
                                                <VerticalFlex>
                                                    <AccentLabel>
                                                        Guest Name
                                                    </AccentLabel>
                                                    <Label>{`${passenger.lastName}, ${passenger.firstName}`}</Label>
                                                </VerticalFlex>

                                                {passenger.isPrimary && (
                                                    <div>
                                                        <PadContainer
                                                            cssClassModifiers={[
                                                                'pad-small',
                                                            ]}
                                                        />
                                                        <span className="confirmation-card__badge confirmation-card__badge--primary-guest">
                                                            Primary Guest
                                                        </span>
                                                    </div>
                                                )}
                                            </TicketHeader>
                                            <TicketBody>
                                                <HorizontalFlex
                                                    cssClassModifiers={[
                                                        'align-flex-start',
                                                    ]}
                                                >
                                                    <VerticalFlex
                                                        cssClassModifiers={[
                                                            'small-relative',
                                                        ]}
                                                    >
                                                        <AccentLabel>
                                                            Airline Code
                                                        </AccentLabel>
                                                        <Label>
                                                            {
                                                                passenger.airlineCode
                                                            }
                                                        </Label>
                                                    </VerticalFlex>
                                                    <PadContainer
                                                        cssClassModifiers={[
                                                            'pad-small',
                                                        ]}
                                                    />
                                                    <VerticalFlex
                                                        cssClassModifiers={[
                                                            'small-relative',
                                                        ]}
                                                    >
                                                        <AccentLabel>
                                                            Confirmation Number
                                                        </AccentLabel>
                                                        <Label>
                                                            {passenger.pnr}
                                                        </Label>
                                                    </VerticalFlex>
                                                </HorizontalFlex>
                                            </TicketBody>
                                        </Ticket>
                                    </div>
                                ))}
                        </VerticalFlex>
                    </PadContainer>
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                <div className="confirmation-card__subtitle">
                    {t('luggageTagDetailsSubtitle')}
                </div>
                <hr className="confirmation-card__divider" />
                <div className="confirmation-card__container">
                    <PadContainer cssClassModifiers={['pad-small']}>
                        <VerticalFlex>
                            {enrollment.addresses.map((address, index) => (
                                <div key={index}>
                                    {index > 0 && (
                                        <PadContainer
                                            cssClassModifiers={['pad-small']}
                                        />
                                    )}
                                    <Ticket>
                                        <TicketHeader>
                                            <VerticalFlex>
                                                <AccentLabel>
                                                    Number of Tags
                                                </AccentLabel>
                                                <Label>
                                                    {address.numberOfTags}
                                                </Label>
                                            </VerticalFlex>
                                        </TicketHeader>
                                        <TicketBody>
                                            <HorizontalFlex
                                                cssClassModifiers={[
                                                    'align-flex-start',
                                                ]}
                                            >
                                                <VerticalFlex
                                                    cssClassModifiers={[
                                                        'small-relative',
                                                    ]}
                                                >
                                                    <AccentLabel>
                                                        Address
                                                    </AccentLabel>
                                                    <Label>{`${address.addressLine1},`}</Label>
                                                    <Label>
                                                        {`${address.city}, ${
                                                            address.state
                                                        }, ${
                                                            countriesEnglish.find(
                                                                (country) =>
                                                                    country.valueField ===
                                                                    address.country,
                                                            )?.valueField
                                                        }`}
                                                    </Label>
                                                </VerticalFlex>
                                                <VerticalFlex
                                                    cssClassModifiers={[
                                                        'small-relative',
                                                    ]}
                                                >
                                                    <AccentLabel>
                                                        Recipient Name
                                                    </AccentLabel>
                                                    <Label>
                                                        {address.recipientName}
                                                    </Label>
                                                </VerticalFlex>
                                            </HorizontalFlex>
                                        </TicketBody>
                                    </Ticket>
                                </div>
                            ))}
                        </VerticalFlex>
                    </PadContainer>
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                <div className="confirmation-card__subtitle">
                    {t('nextStepsSubtitle')}
                </div>
                <hr className="confirmation-card__divider" />
                <PadContainer cssClassModifiers={['pad-small']}>
                    <VerticalFlex>
                        <span>
                            {location.state?.hasModified ? (
                                t('nextStepsUpdatedDescription1')
                            ) : (
                                <Trans
                                    i18nKey={'nextStepsDescription1'}
                                    components={[
                                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                                        <a href="mailto:bcsupport@bagcheck.com" />,
                                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                                        <a href="tel:+4075057443" />,
                                    ]}
                                />
                            )}
                        </span>
                        <PadContainer cssClassModifiers={['pad-small']} />
                        <span>
                            <Trans
                                i18nKey={'nextStepsDescription2'}
                                components={[
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    <a href="mailto:bcsupport@bagcheck.com" />,
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    <a href="tel:+4075057443" />,
                                ]}
                            />
                        </span>
                    </VerticalFlex>
                </PadContainer>
                {/* <PadContainer cssClassModifiers={['pad-small']}>
                    <span>
                        {t('cancelEnrollment')}{' '}
                        <Link to={`cancelEnrollment`}>click here</Link>
                    </span>
                </PadContainer> */}
            </div>
        </div>
    );
}

export default ReservationConfirmationPage;
